import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit';
import formatDate from '../formatDate';
import UserService, { EmptyUserModel, IUserModel } from '../../services/user';
import { IDataDisplayerPagination } from '../../components/DataDisplayer/DataDisplayer';
import { useSelector } from 'react-redux';
import { RootState } from '../../app/store';
import { ISearchParam } from '../BaseApp';

interface IUserInitialState {
    details: IUserModel,
    isLoaded: boolean,
    users: IUserModel[],
    selectedUsers: IUserModel[],
    searchParams: ISearchParam[],
    loading: boolean,
    pagination: IDataDisplayerPagination,
    search?: string,
    showSideModal: boolean,
    errorStatus: number | null,
}

const initialState: IUserInitialState = {
    details: {
        ...EmptyUserModel
    },
    isLoaded: false,
    users: [],
    selectedUsers: [],
    searchParams: [],
    loading: false,
    pagination: {
        count: 0,
        current_page: 0,
        total_pages: 0
    },
    showSideModal: false,
    errorStatus: null
}

export const currentUserDetails = createAsyncThunk(
    'users/account',
    async ({}, { rejectWithValue }) => {
      try {
        const response = await UserService.getCurrentUserData();
        return response;
      } catch (error: any) {
        return rejectWithValue({
            message: "Error",
            status: error.response?.status,
          });
      }
    }
);

const userSlice = createSlice({
    name: "user",
    initialState,
    reducers: {
        updateUser: (state: IUserInitialState, action: PayloadAction<IUserModel>) => {
            state.details = action.payload
            state.isLoaded = true
        },
        updateShowSideModal: (state: IUserInitialState, action: PayloadAction<boolean>) => {
            state.showSideModal = action.payload
        },
        updateAllUsers: (state: IUserInitialState, action: PayloadAction<Array<IUserModel>>) => {
          state.users = action.payload
        },
        addNewUser: (state: IUserInitialState, action: PayloadAction<IUserModel>) => {
            let users = state.users

            if (users.filter(user => user.id === action.payload.id).length === 0){
                // state.transactions.push(action.payload)
                users = [
                    action.payload,
                    ...users
                ]
            }

            state.users = users

        },
        deleteUser: (state: IUserInitialState, action: PayloadAction<{ id: number }>) => {
            const payload = action.payload
            
            let users = state.users

            users = users.filter(user => user.id !== payload.id)

            state.users = users

        },
        updateSingleUser: (state: IUserInitialState, action: PayloadAction<IUserModel>) => {
            let users = state.users
            
            if (users && users.length > 0){
              users.map((user) => {
                    if (user.id === action.payload.id){

                        const locationIndex = users.findIndex(user => user.id === action.payload.id)
                        users[locationIndex] = action.payload

                    }
                })
            }

            state.users = users

        },
        updateUsersLoadingStatus: (state: IUserInitialState, action: PayloadAction<{ loading: boolean }>) => {
            state.loading = action.payload.loading
        },
        updateUsersPagination: (state: IUserInitialState, action: PayloadAction<IDataDisplayerPagination>) => {
            state.pagination = action.payload
        },
        updateUsersSearch: (state: IUserInitialState, action: PayloadAction<{ search: string }>) => {
            state.search = action.payload.search
        },
        updateUsersSearchParams: (state: IUserInitialState, action: PayloadAction<ISearchParam[]>) => {
            state.searchParams = action.payload
        },
        setSelectedUsers: (state: IUserInitialState, action: PayloadAction<IUserModel[]>) => {
            state.selectedUsers = action.payload
        },
    },
    extraReducers: (builder) => {
        builder
          .addCase(currentUserDetails.pending, (state) => {
            state.isLoaded = false
          })
          .addCase(currentUserDetails.fulfilled, (state, action: PayloadAction<IUserModel>) => {
            
            state.details = action.payload
            state.isLoaded = true
          })
          .addCase(currentUserDetails.rejected, (state, action: PayloadAction<any>) => {
            state.isLoaded = false
            console.log("action.payload?", action.payload)
          });
      },
})

/**
 * Return all users
 */
export const useReturnAllUsers = () => useSelector((state: RootState) => state.user.users)

/**
 * Return current user
 */
export const useReturnCurrentUser = () => useSelector((state: RootState) => state.user.details)

/**
 * Retrun single user
 */
export const useReturnSingleUsers = (id: number) => {
    const users = useReturnAllUsers()

    let user: IUserModel = EmptyUserModel

    if (users.length > 0){
      users.map((singleUser) => {
            if (singleUser.id === id){
                user = singleUser
            }
        })
    }

    return user

}

export const returnSingleUser = (id: number, users: IUserModel[]) => {
    let user: IUserModel = EmptyUserModel

    if (users.length > 0){
      users.map((singleUser) => {
            if (singleUser.id === id){
                user = singleUser
            }
        })
    }

    return user

}

/**
 * Return users loading status
 */
export const useReturnUsersLoadingStatus = () => useSelector((state: RootState) => state.user.loading)

/**
 * Return users pagination
 */
export const useReturnUsersPagination = () => useSelector((state: RootState) => state.user.pagination)

/**
 * Return users search
 */
export const useReturnUsersSearch = () => useSelector((state: RootState) => state.user.search)

/**
 * Return users search params
 */
export const useReturnUsersSearchParams = () => useSelector((state: RootState) => state.user.searchParams)

/**
 * Return selected users
 */
export const useReturnSelectedUsers = () => useSelector((state: RootState) => state.user.selectedUsers)

/**
 * Return users side modal
 */
export const useReturnUserSideModalStatus = () => useSelector((state: RootState) => state.user.showSideModal)

export const { updateUser, updateAllUsers, addNewUser, deleteUser, updateSingleUser, updateUsersLoadingStatus, updateUsersPagination, updateUsersSearch, updateShowSideModal, updateUsersSearchParams, setSelectedUsers } = userSlice.actions
export default userSlice.reducer